import {
    Directive,
    DoCheck,
    EmbeddedViewRef,
    Input,
    IterableChangeRecord,
    IterableChanges,
    IterableDiffer,
    IterableDiffers,
    NgIterable,
    NgZone,
    OnDestroy,
    TemplateRef,
    TrackByFunction,
    ViewContainerRef,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { Grid } from "./s25.virtual.grid.component";
import { GridUtil } from "./s25.grid.util";
import { S25ScrollUtil } from "../../standalone/s25-scroll-minimap/s25.scroll.util";

@Directive({
    selector: "[virtualGrid][virtualGridOf]",
    standalone: true,
})
export class S25VirtualGridDirective<Item extends Grid._Item<unknown>, Iter extends Item[] = Item[]>
    implements DoCheck, OnDestroy
{
    @Input({ required: true })
    set virtualGridOf(list: (Iter & NgIterable<Item>) | undefined | null) {
        this.list = list;
        this.isDirty = true;
    }

    @Input()
    set virtualGridTrackBy(fn: TrackByFunction<Item>) {
        this.trackBy = fn;
    }

    get virtualGridTrackBy(): TrackByFunction<Item> {
        return this.trackBy;
    }

    @Input({ required: true })
    set virtualGridRows(rows: number) {
        this.rows = rows ?? 100;
        this.onChanges();
    }

    @Input({ required: true })
    set virtualGridColumns(columns: number) {
        this.columns = columns ?? 100;
        this.onChanges();
    }

    @Input({ required: true })
    set virtualGridScrollElementX(scrollElementX: HTMLElement) {
        this.scrollElementX = scrollElementX;
        this.onChanges();
    }

    @Input({ required: true })
    set virtualGridScrollElementY(scrollElementY: HTMLElement) {
        this.scrollElementY = scrollElementY;
        this.onChanges();
    }

    @Input({ required: true })
    set virtualGridWindowElement(windowElement: HTMLElement) {
        this.windowElement = windowElement;
        this.onChanges();
    }

    list: Iter | undefined | null = null;
    isDirty: boolean = true;
    differ: IterableDiffer<Item> | null = null;
    visibleDiffer: IterableDiffer<Item> | null = null;
    trackBy: TrackByFunction<Item>;
    rows: number;
    columns: number;
    index: ReturnType<typeof GridUtil.createItemIndex<Item>> = new Map();
    scrollElementX: HTMLElement;
    scrollElementY: HTMLElement;
    windowElement: HTMLElement;
    draggingItem: Item;
    resizeObserver: ResizeObserver;
    rendered: Item[] = [];

    constructor(
        private viewContainerRef: ViewContainerRef,
        private templateRef: TemplateRef<VirtualGridOfContext<Item, Iter>>,
        private differs: IterableDiffers,
        private zone: NgZone,
    ) {}

    @Input()
    set virtualGridTemplate(value: TemplateRef<VirtualGridOfContext<Item, Iter>>) {
        if (value) {
            this.templateRef = value;
        }
    }

    ngDoCheck(): void {
        if (this.isDirty) {
            this.isDirty = false;
            const value = this.list;
            if (!this.differ && value) {
                this.differ = this.differs.find(value).create(this.virtualGridTrackBy);
            }
        }
        if (this.differ) {
            const changes = this.differ.diff(this.list);
            if (changes) {
                this.onChanges();
            }
        }
    }

    buildIndex() {
        this.index = GridUtil.createItemIndex(this.list, this.rows, this.columns, (item) => item._gridData);
    }

    onChanges = S25Util.throttled(
        () => {
            if (!this.rows || !this.columns || !this.list) return;
            if (!this.scrollElementX || !this.scrollElementY || !this.windowElement) return;

            this.removeEventListeners();
            this.setEventListeners();
            this.buildIndex();
            this.render();
        },
        10,
        true,
    );

    render = S25Util.throttled(
        () => {
            const { offsetBottom, offsetRight, offsetTop, offsetLeft } = S25ScrollUtil.getScrollLimit(
                this.scrollElementX,
                this.scrollElementY,
                this.windowElement,
            );

            const scrollHeight = this.scrollElementY.scrollHeight - offsetTop - offsetBottom;
            const scrollWidth = this.scrollElementX.scrollWidth - offsetLeft - offsetRight;
            const topFraction = S25Util.clamp((this.scrollElementY.scrollTop - offsetTop) / scrollHeight, 0, 1);
            const leftFraction = S25Util.clamp((this.scrollElementX.scrollLeft - offsetLeft) / scrollWidth, 0, 1);
            let heightFraction = Math.min(this.windowElement.clientHeight, window.innerHeight) / scrollHeight;
            heightFraction = S25Util.clamp(heightFraction, 0, 1);
            let widthFraction = Math.min(this.windowElement.clientWidth, window.innerWidth) / scrollWidth;
            widthFraction = S25Util.clamp(widthFraction, 0, 1);

            const firstRow = Math.floor(topFraction * this.rows);
            const firstColumn = Math.floor(leftFraction * this.columns);
            const lastRow = Math.ceil((topFraction + heightFraction) * this.rows);
            const lastColumn = Math.ceil((leftFraction + widthFraction) * this.columns);

            const visibleItems = GridUtil.getItemsInRegion(this.index, firstRow, lastRow, firstColumn, lastColumn);
            const visibleItemsSet = new Set(visibleItems);

            // Always include items being moved
            for (let item of this.list) {
                if (visibleItemsSet.has(item)) continue;
                const isMoving = item._gridData.dragging() || !!item._gridData.moveStart;
                if (isMoving) visibleItems.push(item);
            }

            this.visibleDiffer ??= this.differs.find([]).create(this.virtualGridTrackBy);
            const diffs = this.visibleDiffer.diff(visibleItems);
            this.applyChanges(diffs);
        },
        10,
        true,
    );

    applyChanges(changes: IterableChanges<Item>) {
        if (!changes) return;
        changes.forEachOperation((change, adjustedPreviousIndex: number | null, currentIndex: number | null) => {
            if (change.previousIndex == null) {
                this.viewContainerRef.createEmbeddedView(
                    this.templateRef,
                    new VirtualGridOfContext<Item, Iter>(change.item, this.list, -1, -1),
                    currentIndex === null ? undefined : currentIndex,
                );
            } else if (currentIndex == null) {
                this.viewContainerRef.remove(adjustedPreviousIndex === null ? undefined : adjustedPreviousIndex);
            } else if (adjustedPreviousIndex !== null) {
                const view = this.viewContainerRef.get(adjustedPreviousIndex);
                this.viewContainerRef.move(view, currentIndex);
                applyViewChange(view as EmbeddedViewRef<VirtualGridOfContext<Item, Iter>>, change);
            }
        });
    }

    ngOnDestroy() {
        this.removeEventListeners();
    }

    removeEventListeners() {
        document.removeEventListener("scroll", this.render, true);
        const xChildren = (this.scrollElementX?.children as unknown as HTMLElement[]) || [];
        const yChildren = (this.scrollElementY?.children as unknown as HTMLElement[]) || [];
        for (let child of xChildren) this.resizeObserver?.unobserve(child);
        for (let child of yChildren) this.resizeObserver?.unobserve(child);
        this.resizeObserver = null;
    }

    setEventListeners() {
        if (!this.windowElement || !this.scrollElementX || !this.scrollElementY) return;

        this.zone.runOutsideAngular(() => {
            document.addEventListener("scroll", this.render, true);
        });
        this.resizeObserver = new ResizeObserver(this.render);
        const xChildren = (this.scrollElementX?.children as unknown as HTMLElement[]) || [];
        const yChildren = (this.scrollElementY?.children as unknown as HTMLElement[]) || [];
        for (let child of xChildren) this.resizeObserver.observe(child);
        for (let child of yChildren) this.resizeObserver.observe(child);
    }

    // Used in tests
    renderAll() {
        this.viewContainerRef.clear();
        for (const item of this.list) {
            this.viewContainerRef.createEmbeddedView(
                this.templateRef,
                new VirtualGridOfContext<Item, Iter>(item, this.list, -1, -1),
            );
        }
    }

    static ngTemplateContextGuard<Item extends Grid._Item<unknown>, Iter extends NgIterable<Item>>(
        directive: S25VirtualGridDirective<Item, Item[]>,
        context: unknown,
    ): context is VirtualGridOfContext<Item, Iter> {
        return true;
    }
}

function applyViewChange<Item extends Grid._Item<unknown>>(
    view: EmbeddedViewRef<VirtualGridOfContext<Item>>,
    record: IterableChangeRecord<Item>,
) {
    view.context.$implicit = record.item;
}

export class VirtualGridOfContext<Item extends Grid._Item<unknown>, Iter extends NgIterable<Item> = NgIterable<Item>> {
    constructor(
        public $implicit: Item,
        public virtualGridOf: Iter,
        public index: number,
        public count: number,
    ) {}
}
